/**
 * HEIC / HEIF — platform-decoded image viewing (§8, §20, §32).
 *
 * The container metadata is read here; the pixels are not. §32's cost model and
 * the licensing decision behind it rule out bundling an HEVC decoder (the good
 * open one is LGPL-3.0), so decoding is delegated to the platform's own image
 * pipeline through an `<img>` — the same "the platform has the codec, we do
 * not" stance §20 takes for audio and video. Where the platform can decode HEIC
 * (Apple systems, Windows with the HEVC extension, Safari), the photo renders;
 * where it cannot (Chromium as shipped), the viewer says so and still shows the
 * real dimensions and orientation read from the container.
 *
 * This is why the model carries no pixels: there is no second, in-app decoding
 * path that could disagree with the platform's. Editing is `convert-edit` in
 * the catalog and no converter ships, so the handler exposes no `exportBytes`
 * and opens read-only — §19, a viewer with no Edit button is the honest shape
 * of "we can show it, not change it".
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { isHeif, parseHeif } from './heif.js';

export interface NativeImageModel {
  readonly kind: 'native-image';
  readonly formatId: string;
  /** MIME type for the `<img>` source. */
  readonly mimeType: string;
  readonly width: number | null;
  readonly height: number | null;
  /** Clockwise display rotation in degrees. */
  readonly rotationDeg: number;
  readonly brand: string;
}

export const heicHandler: FormatHandler<NativeImageModel> = {
  id: 'heic',
  formatIds: ['heic'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    if (!isHeif(source.bytes)) {
      throw new HandlerError('error.damagedOrIncomplete', 'not a HEIF/HEIC file');
    }
    const info = parseHeif(source.bytes);
    return {
      // 'image' so it flows to the image area; the shell renders HEIC through
      // the native-image path rather than the pixel canvas.
      kind: 'image',
      unitCount: undefined,
      unitKey: undefined,
      firstContent: {
        native: true,
        mimeType: 'image/heic',
        width: info?.width ?? null,
        height: info?.height ?? null,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<NativeImageModel>> {
    throwIfAborted(ctx.signal);
    if (!isHeif(source.bytes)) {
      throw new HandlerError('error.damagedOrIncomplete', 'not a HEIF/HEIC file');
    }
    const info = parseHeif(source.bytes);
    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    warnings.push({
      messageKey: 'status.limitedSupport',
      severity: 'info',
      detail: 'HEIC is decoded by the platform’s own image support. Where the device lacks it (this build of Chromium does), the picture is not shown and its dimensions are reported instead — no HEVC decoder is bundled (§20, §32).',
    });
    if (source.sizeBytes > 40 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large image (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        kind: 'native-image',
        formatId: source.formatId,
        mimeType: 'image/heic',
        width: info?.width ?? null,
        height: info?.height ?? null,
        rotationDeg: info?.rotationDeg ?? 0,
        brand: info?.brand ?? '',
      },
      readOnly: true,
      editLevel: spec?.edit ?? 'convert-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  // No exportBytes: HEIC is not edited in this build (§19).
};
